import {
	ChevronRight,
	ExternalLink,
	Pencil,
	Plus,
	Search,
	Trash2,
	X,
} from "lucide-react";
import { Fragment, type FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	type Matter,
	type RestrictedWord,
	type RestrictedWordRule,
	useMattersCache,
	useRestrictedWords,
} from "@/shared/cache";
import { ComboBox } from "@/shared/components/ComboBox/ComboBox";
import { NarrativeTextArea } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import { Button } from "@/shared/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Textarea } from "@/shared/components/ui/textarea";
import { matterScopeParts } from "@/shared/utils/matterScope";
import { useApi } from "@/shared/utils/request";
import { detectRestrictedWords } from "@/shared/utils/restrictedWords";
import { OCG_DOCUMENTS } from "../fixtures/ocgs";
import styles from "./AdminViews.module.css";

// Mirrors the dashboard's tab styling so the two admin pages read as one product.
const complianceTabTriggerClass =
	"rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2.5 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";

type RuleFormState = {
	regexPattern: string;
	displayName: string;
	suggestion: string;
	clientCode: string;
	matterId: string;
	warning: boolean;
	disabled: boolean;
};

const EMPTY_FORM: RuleFormState = {
	regexPattern: "",
	displayName: "",
	suggestion: "",
	clientCode: "",
	matterId: "",
	warning: false,
	disabled: false,
};

type ClientOption = {
	code: string;
	name: string;
};

const clientSearchFields = [
	(client: ClientOption) => ({ value: client.code, weight: 1.5 }),
	(client: ClientOption) => ({ value: client.name, weight: 1 }),
];

const matterSearchFields = [
	(matter: Matter) => ({ value: matterDisplayNumber(matter), weight: 1.5 }),
	(matter: Matter) => ({ value: matter.description, weight: 1 }),
];

function clientOptionLabel(client: ClientOption): string {
	return client.name && client.name !== client.code
		? `${client.code} — ${client.name}`
		: client.code;
}

/**
 * Label a matter by its human matter number where the tenant has one. The scope
 * key can be an opaque id, which is unreadable in a picker.
 */
function matterDisplayNumber(matter: Matter): string {
	return matter.matter?.trim() || matterScopeParts(matter).matterId;
}

function matterOptionLabel(matter: Matter): string {
	const number = matterDisplayNumber(matter);
	const description = matter.description.trim();
	return description ? `${number} — ${description}` : number;
}

function formStateForRule(rule: RestrictedWordRule | null): RuleFormState {
	if (!rule) return EMPTY_FORM;
	return {
		regexPattern: rule.regexPattern,
		displayName: rule.displayName,
		suggestion: rule.suggestion ?? "",
		clientCode: rule.clientCode ?? "",
		matterId: rule.matterId ?? "",
		warning: rule.warning,
		disabled: rule.disabled,
	};
}

/** Broadest first — a rule's tier decides which band it is listed under. */
const SCOPE_TIERS = ["firm", "client", "matter"] as const;
type ScopeTier = (typeof SCOPE_TIERS)[number];

const SCOPE_TIER_LABEL: Record<ScopeTier, string> = {
	firm: "Firm-wide",
	client: "Client-specific",
	matter: "Matter-specific",
};

function ruleTier(rule: RestrictedWordRule): ScopeTier {
	if (!rule.clientCode) return "firm";
	return rule.matterId ? "matter" : "client";
}

/** Codes are what the API stores; people recognise the names. */
type ScopeNames = {
	clients: Map<string, string>;
	matters: Map<string, string>;
};

function namedScope(rule: RestrictedWordRule, names: ScopeNames): string {
	if (!rule.clientCode) return "Firm-wide";
	const client = names.clients.get(rule.clientCode) ?? rule.clientCode;
	if (!rule.matterId) return client;
	return `${client} · ${names.matters.get(rule.matterId) ?? rule.matterId}`;
}

/**
 * One row per distinct word rather than per scope: the same word configured for
 * five clients is five API records, which read as five duplicate rows. The
 * scopes stay reachable by expanding the row.
 */
type RuleGroup = {
	key: string;
	displayName: string;
	regexPattern: string;
	suggestion: string | null;
	warning: boolean;
	tier: ScopeTier;
	rules: RestrictedWordRule[];
};

function groupRules(rules: RestrictedWordRule[]): RuleGroup[] {
	const byWord = new Map<string, RuleGroup>();
	for (const rule of rules) {
		const key = JSON.stringify([rule.displayName, rule.regexPattern]);
		const group = byWord.get(key);
		if (!group) {
			byWord.set(key, {
				key,
				displayName: rule.displayName,
				regexPattern: rule.regexPattern,
				suggestion: rule.suggestion,
				warning: rule.warning,
				tier: ruleTier(rule),
				rules: [rule],
			});
			continue;
		}
		group.rules.push(rule);
		group.suggestion = group.suggestion ?? rule.suggestion;
		group.warning = group.warning || rule.warning;
		// Broadest scope wins so a word never appears under two bands.
		if (SCOPE_TIERS.indexOf(ruleTier(rule)) < SCOPE_TIERS.indexOf(group.tier)) {
			group.tier = ruleTier(rule);
		}
	}
	return [...byWord.values()].sort((left, right) =>
		left.displayName.localeCompare(right.displayName),
	);
}

function RuleDialog({
	open,
	rule,
	onOpenChange,
	onSaved,
}: {
	open: boolean;
	rule: RestrictedWordRule | null;
	onOpenChange: (open: boolean) => void;
	onSaved: () => Promise<void>;
}) {
	const { makeApiRequest } = useApi();
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
	const [testNarrative, setTestNarrative] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const draftRuleTest = useMemo<{
		restrictedWords: RestrictedWord[];
		error: string | null;
	}>(() => {
		const regexPattern = form.regexPattern.trim();
		if (!regexPattern) return { restrictedWords: [], error: null };

		try {
			return {
				restrictedWords: [
					{
						word: regexPattern,
						pattern: new RegExp(regexPattern, "gi"),
						displayName: form.displayName.trim() || "Draft rule",
						suggestion: form.suggestion.trim() || null,
						severity: form.warning ? "warning" : "block",
					},
				],
				error: null,
			};
		} catch (error) {
			return {
				restrictedWords: [],
				error:
					error instanceof Error
						? `Invalid regular expression: ${error.message}`
						: "Invalid regular expression.",
			};
		}
	}, [form.displayName, form.regexPattern, form.suggestion, form.warning]);
	const testMatches = useMemo(
		() => detectRestrictedWords(testNarrative, draftRuleTest.restrictedWords),
		[draftRuleTest.restrictedWords, testNarrative],
	);
	const clientOptions = useMemo(() => {
		const clientsByCode = new Map<string, ClientOption>();
		for (const matter of matters) {
			const { clientCode } = matterScopeParts(matter);
			if (!clientCode || clientsByCode.has(clientCode)) continue;
			clientsByCode.set(clientCode, {
				code: clientCode,
				name: matter.client.trim(),
			});
		}
		return [...clientsByCode.values()].sort((left, right) =>
			left.code.localeCompare(right.code),
		);
	}, [matters]);
	const matterOptions = useMemo(
		() =>
			matters
				.filter((matter) => {
					const scope = matterScopeParts(matter);
					return scope.clientCode === form.clientCode && !!scope.matterId;
				})
				.sort((left, right) =>
					matterScopeParts(left).matterId.localeCompare(
						matterScopeParts(right).matterId,
					),
				),
		[form.clientCode, matters],
	);

	useEffect(() => {
		if (!open) return;
		setForm(formStateForRule(rule));
		setTestNarrative("");
		setFormError(null);
	}, [open, rule]);

	const update = <K extends keyof RuleFormState>(
		field: K,
		value: RuleFormState[K],
	) => setForm((current) => ({ ...current, [field]: value }));

	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const regexPattern = form.regexPattern.trim();
		const displayName = form.displayName.trim();
		const clientCode = form.clientCode.trim() || null;
		const matterId = form.matterId.trim() || null;

		if (!regexPattern || !displayName) {
			setFormError("Pattern and display name are required.");
			return;
		}
		if (draftRuleTest.error) {
			setFormError(draftRuleTest.error);
			return;
		}
		if (matterId && !clientCode) {
			setFormError("A client code is required for a matter-specific rule.");
			return;
		}

		setSubmitting(true);
		setFormError(null);
		try {
			const response = await makeApiRequest({
				endpoint: rule
					? `/v2/restricted-words/${encodeURIComponent(rule.id)}`
					: "/v2/restricted-words",
				method: rule ? "PATCH" : "POST",
				body: {
					regexPattern,
					displayName,
					suggestion: form.suggestion.trim() || null,
					warning: form.warning,
					disabled: form.disabled,
					clientCode,
					matterId,
				},
			});
			if (response.status === "ERROR") {
				setFormError(response.error || "Failed to save restricted word.");
				return;
			}

			await onSaved();
			toast.success(
				rule ? "Restricted word updated" : "Restricted word created",
			);
			onOpenChange(false);
		} catch (error) {
			setFormError(
				error instanceof Error
					? error.message
					: "Failed to save restricted word.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="overflow-visible sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{rule ? "Edit restricted word" : "Add restricted word"}
					</DialogTitle>
					<DialogDescription>
						Patterns are regular expressions. Leave both scope fields empty for
						a firm-wide rule.
					</DialogDescription>
				</DialogHeader>

				<form className="space-y-4" onSubmit={submit}>
					<div className="space-y-2 rounded-md border border-border p-3">
						<div className="flex flex-wrap items-baseline justify-between gap-2">
							<Label htmlFor="restricted-word-test-narrative">
								Test narrative
							</Label>
							<span className="text-xs text-muted-foreground">
								Tests only this draft rule
							</span>
						</div>
						<NarrativeTextArea
							id="restricted-word-test-narrative"
							value={testNarrative}
							onChange={setTestNarrative}
							placeholder="Type or paste a narrative to test the pattern"
							restrictedWords={draftRuleTest.restrictedWords}
							error={draftRuleTest.error ?? undefined}
							className="min-h-20 max-h-28 resize-y"
						/>
						{!draftRuleTest.error && !form.regexPattern.trim() && (
							<p className="text-xs text-muted-foreground">
								Enter a regex pattern to begin testing.
							</p>
						)}
						{!draftRuleTest.error &&
							!!form.regexPattern.trim() &&
							!!testNarrative && (
								<output
									className={
										testMatches.length === 0
											? "text-xs text-emerald-600 dark:text-emerald-400"
											: form.warning
												? "text-xs text-amber-600 dark:text-amber-400"
												: "text-xs text-destructive"
									}
								>
									{testMatches.length === 0
										? "No matches for this rule."
										: `${form.warning ? "Warning" : "Blocked"}: ${testMatches.length} ${testMatches.length === 1 ? "match" : "matches"}.`}
								</output>
							)}
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-name">Display name</Label>
							<Input
								id="restricted-word-name"
								value={form.displayName}
								onChange={(event) => update("displayName", event.target.value)}
								maxLength={100}
								required
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-pattern">Regex pattern</Label>
							<Input
								id="restricted-word-pattern"
								className="font-mono"
								value={form.regexPattern}
								onChange={(event) => update("regexPattern", event.target.value)}
								maxLength={255}
								required
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="restricted-word-suggestion">Suggestion</Label>
						<Textarea
							id="restricted-word-suggestion"
							value={form.suggestion}
							onChange={(event) => update("suggestion", event.target.value)}
							maxLength={500}
							placeholder="Optional replacement guidance"
						/>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-client">Client code</Label>
							<ComboBox
								id="restricted-word-client"
								items={clientOptions}
								value={form.clientCode || null}
								onChange={(clientCode) =>
									setForm((current) => ({
										...current,
										clientCode,
										matterId: "",
									}))
								}
								fields={clientSearchFields}
								getKey={(client) => client.code}
								getDisplayLabel={clientOptionLabel}
								placeholder="Select a client (optional)"
								disabled={mattersLoading}
								disabledMessage="Loading clients…"
								clearable
								onClear={() =>
									setForm((current) => ({
										...current,
										clientCode: "",
										matterId: "",
									}))
								}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-matter">Matter ID</Label>
							<ComboBox
								id="restricted-word-matter"
								items={matterOptions}
								value={form.matterId || null}
								onChange={(matterId) => update("matterId", matterId)}
								fields={matterSearchFields}
								getKey={(matter) => matterScopeParts(matter).matterId}
								getDisplayLabel={matterOptionLabel}
								placeholder="Select a matter (optional)"
								disabled={!form.clientCode || mattersLoading}
								disabledMessage={
									mattersLoading ? "Loading matters…" : "Select a client first"
								}
								clearable
								onClear={() => update("matterId", "")}
							/>
						</div>
					</div>

					<div className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2">
						<label
							htmlFor="restricted-word-warning"
							className="flex items-center justify-between gap-3 text-sm"
						>
							<span>
								<span className="block font-medium">Warning only</span>
								<span className="text-xs text-muted-foreground">
									Allow input but show a warning
								</span>
							</span>
							<Switch
								id="restricted-word-warning"
								checked={form.warning}
								onCheckedChange={(checked) => update("warning", checked)}
							/>
						</label>
						<label
							htmlFor="restricted-word-disabled"
							className="flex items-center justify-between gap-3 text-sm"
						>
							<span>
								<span className="block font-medium">Disabled</span>
								<span className="text-xs text-muted-foreground">
									Keep the rule without enforcing it
								</span>
							</span>
							<Switch
								id="restricted-word-disabled"
								checked={form.disabled}
								onCheckedChange={(checked) => update("disabled", checked)}
							/>
						</label>
					</div>

					{formError && (
						<p className="text-sm text-destructive" role="alert">
							{formError}
						</p>
					)}

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" loading={submitting}>
							{rule ? "Save changes" : "Create rule"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

export default function RestrictedWordsView() {
	const { makeApiRequest } = useApi();
	const { rules, isLoading, error, refresh } = useRestrictedWords();
	const [search, setSearch] = useState("");
	const [activeTab, setActiveTab] = useState("ocgs");
	// Set when a guideline is opened from the OCGs tab. Exact-match on
	// clientCode rather than reusing `search`, which is substring across five
	// fields and would sweep in unrelated matter ids.
	const [clientFilter, setClientFilter] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingRule, setEditingRule] = useState<RestrictedWordRule | null>(
		null,
	);
	const [mutatingId, setMutatingId] = useState<string | null>(null);
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
	const [collapsedBands, setCollapsedBands] = useState<Set<ScopeTier>>(
		new Set(),
	);
	const { matters } = useMattersCache();

	const toggleBand = (tier: ScopeTier) =>
		setCollapsedBands((current) => {
			const next = new Set(current);
			if (!next.delete(tier)) next.add(tier);
			return next;
		});

	const toggleExpanded = (key: string) =>
		setExpandedKeys((current) => {
			const next = new Set(current);
			if (!next.delete(key)) next.add(key);
			return next;
		});

	// Guidelines report the real rule count for their client, so the OCGs tab
	// can never claim rules the Billing Rules tab does not have.
	const rulesByClient = useMemo(() => {
		const counts = new Map<string, number>();
		for (const rule of rules) {
			if (!rule.clientCode) continue;
			counts.set(rule.clientCode, (counts.get(rule.clientCode) ?? 0) + 1);
		}
		return counts;
	}, [rules]);

	const openGuidelineRules = (clientCode: string) => {
		setClientFilter(clientCode);
		setSearch("");
		setActiveTab("billing-rules");
	};

	const scopeNames = useMemo<ScopeNames>(() => {
		const clients = new Map<string, string>();
		const matterNames = new Map<string, string>();
		for (const matter of matters) {
			const { clientCode, matterId } = matterScopeParts(matter);
			const clientName = matter.client.trim();
			if (clientCode && clientName && !clients.has(clientCode)) {
				clients.set(clientCode, clientName);
			}
			const description = matter.description.trim();
			if (matterId && description && !matterNames.has(matterId)) {
				matterNames.set(matterId, description);
			}
		}
		return { clients, matters: matterNames };
	}, [matters]);

	const filteredRules = useMemo(() => {
		const query = search.trim().toLowerCase();
		return rules
			.filter((rule) => {
				if (clientFilter && rule.clientCode !== clientFilter) return false;
				if (!query) return true;
				return [
					rule.displayName,
					rule.regexPattern,
					rule.suggestion,
					rule.clientCode,
					rule.matterId,
				].some((value) => value?.toLowerCase().includes(query));
			})
			.sort((left, right) => left.displayName.localeCompare(right.displayName));
	}, [clientFilter, rules, search]);

	// Searching flattens the bands: a hit is easier to spot in one list than
	// scattered across three headed sections.
	const visibleBands = useMemo<
		Array<{ tier: ScopeTier | null; groups: RuleGroup[] }>
	>(() => {
		const groups = groupRules(filteredRules);
		if (search.trim() || clientFilter) return [{ tier: null, groups }];
		return SCOPE_TIERS.map((tier) => ({
			tier,
			groups: groups.filter((group) => group.tier === tier),
		})).filter((band) => band.groups.length > 0);
	}, [clientFilter, filteredRules, search]);

	const openCreate = () => {
		setEditingRule(null);
		setDialogOpen(true);
	};

	const openEdit = (rule: RestrictedWordRule) => {
		setEditingRule(rule);
		setDialogOpen(true);
	};

	const updateDisabled = async (
		rule: RestrictedWordRule,
		disabled: boolean,
	) => {
		setMutatingId(rule.id);
		try {
			const response = await makeApiRequest({
				endpoint: `/v2/restricted-words/${encodeURIComponent(rule.id)}`,
				method: "PATCH",
				body: { disabled },
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to update restricted word.");
			}
			await refresh();
			toast.success(
				disabled ? "Restricted word disabled" : "Restricted word enabled",
			);
		} catch (mutationError) {
			toast.error(
				mutationError instanceof Error
					? mutationError.message
					: "Failed to update restricted word.",
			);
		} finally {
			setMutatingId(null);
		}
	};

	const deleteRule = async (rule: RestrictedWordRule) => {
		if (
			!window.confirm(
				`Permanently delete “${rule.displayName}”? This cannot be undone.`,
			)
		) {
			return;
		}

		setMutatingId(rule.id);
		try {
			const response = await makeApiRequest({
				endpoint: `/v2/restricted-words/${encodeURIComponent(rule.id)}`,
				method: "DELETE",
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to delete restricted word.");
			}
			await refresh();
			toast.success("Restricted word deleted");
		} catch (mutationError) {
			toast.error(
				mutationError instanceof Error
					? mutationError.message
					: "Failed to delete restricted word.",
			);
		} finally {
			setMutatingId(null);
		}
	};

	return (
		<main className={styles.adminViewWrapper}>
			<div>
				<h1 className="text-2xl font-semibold text-foreground">Compliance</h1>
			</div>

			<Tabs
				value={activeTab}
				onValueChange={setActiveTab}
				className="mt-6 flex-1"
			>
				<TabsList className="h-auto w-full justify-start gap-2 rounded-none border-b border-border bg-transparent p-0">
					<TabsTrigger value="ocgs" className={complianceTabTriggerClass}>
						OCGs
					</TabsTrigger>
					<TabsTrigger
						value="billing-rules"
						className={complianceTabTriggerClass}
					>
						Billing Rules
					</TabsTrigger>
					<TabsTrigger
						value="deductions"
						disabled
						className={`${complianceTabTriggerClass} disabled:cursor-not-allowed disabled:opacity-50`}
					>
						Deductions Data{" "}
						<span className="ml-1 font-normal">(coming soon)</span>
					</TabsTrigger>
				</TabsList>

				<TabsContent value="ocgs" className="mt-6 pb-16">
					{error && (
						<div role="alert" className="mb-4 text-sm text-destructive">
							Unable to load billing rules.{" "}
							<Button
								variant="outline"
								size="sm"
								onClick={() => void refresh()}
							>
								Try again
							</Button>
						</div>
					)}
					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{OCG_DOCUMENTS.map((doc) => {
							const ruleCount = rulesByClient.get(doc.clientCode) ?? 0;
							return (
								<article key={doc.id} className={styles.ocgCard}>
									<a
										href={doc.file}
										target="_blank"
										rel="noreferrer"
										className={styles.ocgThumb}
										aria-label={`Open ${doc.title} (PDF, ${doc.pages} pages)`}
									>
										<img src={doc.thumb} alt="" loading="lazy" />
										<span className={styles.ocgThumbOverlay}>
											<span className={styles.ocgThumbAction}>
												<ExternalLink className="size-3.5" />
												Open PDF
											</span>
										</span>
										<span className={styles.ocgPages}>{doc.pages} pages</span>
									</a>

									<div className="flex flex-1 flex-col gap-1 p-4">
										<h3 className="font-medium leading-tight text-foreground">
											{doc.clientCode}
										</h3>
										<p className={styles.ocgTitle}>{doc.title}</p>
										<p className="mt-auto pt-3 text-xs text-muted-foreground">
											{doc.effective}
										</p>
									</div>

									<div className={styles.ocgFooter}>
										{isLoading || error ? (
											<span className="text-xs text-muted-foreground">
												{isLoading
													? "Loading rules…"
													: "Rule count unavailable"}
											</span>
										) : ruleCount === 0 ? (
											<span className="text-xs text-muted-foreground">
												Not yet extracted
											</span>
										) : (
											<span className={styles.ocgRuleBadge}>
												{ruleCount} rule{ruleCount === 1 ? "" : "s"} extracted
											</span>
										)}
										<Button
											variant="ghost"
											size="sm"
											disabled={isLoading || !!error || ruleCount === 0}
											onClick={() => openGuidelineRules(doc.clientCode)}
										>
											View rules
											<ChevronRight />
										</Button>
									</div>
								</article>
							);
						})}
					</div>
				</TabsContent>

				<TabsContent value="billing-rules" className="mt-6 pb-16">
					<div className="mb-4 flex items-center justify-between gap-3">
						<div className="relative w-full max-w-sm">
							<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search rules or scopes"
								className="pl-9"
							/>
						</div>
						<Button
							className={`${styles.adminPrimaryButton} shrink-0`}
							onClick={openCreate}
						>
							<Plus /> Add billing rule
						</Button>
					</div>

					{clientFilter && (
						<div className="mb-4 flex items-center gap-2 text-sm">
							<span className="text-muted-foreground">Showing rules from</span>
							<span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 font-medium">
								{clientFilter}
								<button
									type="button"
									onClick={() => setClientFilter(null)}
									className="text-muted-foreground hover:text-foreground"
									aria-label={`Clear the ${clientFilter} filter`}
								>
									<X className="size-3.5" />
								</button>
							</span>
						</div>
					)}

					<section className={styles.compliancePanel}>
						{isLoading ? (
							<p className={`${styles.loadingText} p-6`}>
								Loading billing rules…
							</p>
						) : error ? (
							<div className="space-y-3 p-6">
								<p className="text-sm text-destructive">{error}</p>
								<Button
									variant="outline"
									size="sm"
									onClick={() => void refresh()}
								>
									Try again
								</Button>
							</div>
						) : filteredRules.length === 0 ? (
							<p className={`${styles.emptyText} p-6`}>
								{rules.length === 0
									? "No billing rules have been configured."
									: clientFilter
										? `No billing rules are scoped to ${clientFilter}.`
										: "No billing rules match this search."}
							</p>
						) : (
							<div className="overflow-x-auto">
								<table className={styles.dataTable}>
									<thead>
										<tr>
											<th className="w-8" aria-label="Expand" />
											<th>Rule</th>
											<th>Trigger</th>
											<th>Behavior</th>
											<th>Enabled</th>
											<th aria-label="Actions" />
										</tr>
									</thead>
									<tbody>
										{visibleBands.map(({ tier, groups }) => (
											<Fragment key={tier ?? "all"}>
												{tier && (
													<tr className={styles.dataTableGroupRow}>
														<td colSpan={6}>
															<button
																type="button"
																onClick={() => toggleBand(tier)}
																className="flex w-full items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted"
															>
																<ChevronRight
																	className={`size-3.5 transition-transform ${collapsedBands.has(tier) ? "" : "rotate-90"}`}
																/>
																{SCOPE_TIER_LABEL[tier]}
																<span className="font-normal normal-case">
																	({groups.length})
																</span>
															</button>
														</td>
													</tr>
												)}
												{(!tier || !collapsedBands.has(tier)) &&
													groups.map((group) => {
														const expanded = expandedKeys.has(group.key);
														const multiScope = group.rules.length > 1;
														const allEnabled = group.rules.every(
															(rule) => !rule.disabled,
														);
														const busy = group.rules.some(
															(rule) => mutatingId === rule.id,
														);
														return (
															<Fragment key={group.key}>
																<tr
																	className={`cursor-pointer ${allEnabled ? "" : "opacity-60"}`}
																	onClick={() =>
																		multiScope
																			? toggleExpanded(group.key)
																			: openEdit(group.rules[0])
																	}
																>
																	<td>
																		{multiScope && (
																			<ChevronRight
																				className={`size-4 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`}
																			/>
																		)}
																	</td>
																	<td>
																		<div className="font-medium">
																			{group.displayName}
																		</div>
																		{group.suggestion && (
																			<div className="text-xs text-muted-foreground">
																				{group.suggestion}
																			</div>
																		)}
																		<div className="mt-1 text-xs text-muted-foreground">
																			{multiScope
																				? `${group.rules.length} scopes`
																				: namedScope(
																						group.rules[0],
																						scopeNames,
																					)}
																		</div>
																	</td>
																	<td className="max-w-56">
																		<button
																			type="button"
																			className="block max-w-full text-left"
																			onClick={(event) => {
																				event.stopPropagation();
																				toggleExpanded(group.key);
																			}}
																		>
																			<code
																				className={`rounded bg-muted px-1.5 py-0.5 text-xs ${expanded ? "break-all" : "block truncate"}`}
																			>
																				{group.regexPattern}
																			</code>
																		</button>
																	</td>
																	<td>
																		<span
																			className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
																				group.warning
																					? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
																					: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
																			}`}
																		>
																			{group.warning ? "Warning" : "Block"}
																		</span>
																	</td>
																	<td
																		onClick={(event) => event.stopPropagation()}
																	>
																		<Switch
																			checked={allEnabled}
																			disabled={busy}
																			onCheckedChange={(enabled) => {
																				for (const rule of group.rules) {
																					void updateDisabled(rule, !enabled);
																				}
																			}}
																			aria-label={`${allEnabled ? "Disable" : "Enable"} ${group.displayName}`}
																		/>
																	</td>
																	<td
																		onClick={(event) => event.stopPropagation()}
																	>
																		<div className="flex justify-end gap-1">
																			<Button
																				variant="ghost"
																				size="icon"
																				onClick={() => openEdit(group.rules[0])}
																				aria-label={`Edit ${group.displayName}`}
																			>
																				<Pencil />
																			</Button>
																			{!multiScope && (
																				<Button
																					variant="ghost"
																					size="icon"
																					className="text-destructive hover:text-destructive"
																					disabled={busy}
																					onClick={() =>
																						void deleteRule(group.rules[0])
																					}
																					aria-label={`Delete ${group.displayName}`}
																				>
																					<Trash2 />
																				</Button>
																			)}
																		</div>
																	</td>
																</tr>

																{expanded &&
																	multiScope &&
																	group.rules.map((rule) => (
																		<tr
																			key={rule.id}
																			className={`${styles.dataTableSubRow} cursor-pointer ${rule.disabled ? "opacity-60" : ""}`}
																			onClick={() => openEdit(rule)}
																		>
																			<td />
																			<td colSpan={3} className="pl-6">
																				{namedScope(rule, scopeNames)}
																			</td>
																			<td
																				onClick={(event) =>
																					event.stopPropagation()
																				}
																			>
																				<Switch
																					checked={!rule.disabled}
																					disabled={mutatingId === rule.id}
																					onCheckedChange={(enabled) =>
																						void updateDisabled(rule, !enabled)
																					}
																					aria-label={`${rule.disabled ? "Enable" : "Disable"} ${rule.displayName} for ${namedScope(rule, scopeNames)}`}
																				/>
																			</td>
																			<td
																				onClick={(event) =>
																					event.stopPropagation()
																				}
																			>
																				<div className="flex justify-end gap-1">
																					<Button
																						variant="ghost"
																						size="icon"
																						className="size-7 text-destructive hover:text-destructive"
																						disabled={mutatingId === rule.id}
																						onClick={() =>
																							void deleteRule(rule)
																						}
																						aria-label={`Delete ${rule.displayName} for ${namedScope(rule, scopeNames)}`}
																					>
																						<Trash2 />
																					</Button>
																				</div>
																			</td>
																		</tr>
																	))}
															</Fragment>
														);
													})}
											</Fragment>
										))}
									</tbody>
								</table>
							</div>
						)}
					</section>
				</TabsContent>
			</Tabs>

			<RuleDialog
				open={dialogOpen}
				rule={editingRule}
				onOpenChange={setDialogOpen}
				onSaved={refresh}
			/>
		</main>
	);
}
